import 'leaflet/dist/leaflet.css'
import './HitMap.css'

import React, { useEffect, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet'
import L, { LatLngExpression } from 'leaflet'
import { CurrentSession } from '@/services/repository/session'
import { Services } from '@/services/services'
import { useNavigate } from 'react-router-dom'
import { useModal } from '../modal/Modal'
import { SuperAdminInsertMachinesPopup } from '@/pages/for/superadmin/machines/Machines'
import { HIG_HIT, LOW_HIT, MID_HIT } from '@/services/repository/hits'
import { formatDate } from '@/common/time'
import { Roles } from '@/services/repository/users'
import { defaultMapCenter } from '@/env/config'
import { hitClassColor } from '@/components/hit/HitText'

interface MapRowParams {
  hitX: number
  hitY: number
  hitZ: number
  ucid: string
}

export interface MapRow {
  imei: string
  dt_tracker: string // ISO 8601 string representation of DTTime (e.g., "2025-01-17T12:34:56Z")
  lat: number  // Latitude as a floating-point number
  lng: number  // Longitude as a floating-point number
  altitude: number // Altitude in meters
  angle: number // Angle in degrees
  speed: number // Speed in meters per second
  // machine: MapRowMachine
  machine_id: string
  machine_name: string
  params: MapRowParams
}

interface MapResponse {
  time: string // ISO 8601 string representation of the latest DtServer
  rows: MapRow[] | null   // Array of MapRow objects
}

export const HitMapComponent = ({ services, session, rows }: {
  services: Services,
  session: CurrentSession
  // fetchUrl: string
  rows: MapRow[] | undefined
}) => {
  const navigate = useNavigate()
  const { openModal } = useModal()
  const [myposition, setMyPosition] = useState<[number, number] | undefined>(undefined)

  useEffect(() => {
    // Function to handle geolocation success
    const handleSuccess = (location: GeolocationPosition) => {
      const { latitude, longitude } = location.coords
      setMyPosition([latitude, longitude])
    }

    // Function to handle geolocation error
    const handleError = (error: GeolocationPositionError) => {
      console.error('Error obtaining location:', error)
      // Fallback to a default position if geolocation fails
      setMyPosition(undefined)
    }

    // Check if geolocation is available
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError)
    } else {
      console.error('Geolocation is not supported by this browser.')
      // Fallback to a default position if geolocation is not supported
      setMyPosition(undefined)
    }
  }, [])
  
  if (rows === undefined) {
    return <div className='spinner'></div>
  }

  function findLatestRow(rows: MapRow[]): MapRow | undefined {
    if (!rows || rows.length === 0) {
      return undefined;
    }

    return rows.reduce((latest, current) => {
      return new Date(current.dt_tracker) > new Date(latest.dt_tracker) ? current : latest;
    });
  }

  const firstRow = findLatestRow(rows);

  const center: LatLngExpression = (firstRow && {
    lat: firstRow.lat,
    lng: firstRow.lng
  }) || myposition || defaultMapCenter

  function hitClassPoint(row: MapRow): string {
    const value = Math.max(Math.abs(row.params.hitX), Math.abs(row.params.hitY), Math.abs(row.params.hitZ))
    if (value > HIG_HIT) {
      return 'rgba(247, 25, 0, 0.2)'
    }
    if (value > MID_HIT) {
      return 'rgba(247, 127, 0, 0.2)'
    }
    if (value > LOW_HIT) {
      return 'rgba(255, 215, 0, 0.2)'
    }
    return 'rgba(0, 0, 0, 0.2)'
  }

  return (
    <MapContainer attributionControl={false} center={center} zoom={17} style={{ height: '100%', width: '100%' }} preferCanvas={true}>
      <TileLayer
        attribution='evoltag.pl'
        url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
      />
      {rows?.map((row, i) => {
        let go = () => row.machine_id && navigate(`/communal/machine/${row.machine_id}`)
        return <CircleMarker
          key={i}
          center={row}
          radius={12} // Adjust this to match your visual size
          pathOptions={{
            fillColor: hitClassPoint(row), // or derive from your hitClassPoint()
            color: '#000',
            weight: 1,
            opacity: 1,
            fillOpacity: 0.8,
          }}
        >
          <Popup>
            <div>
              {!row.machine_id ? <p style={{ cursor: 'pointer' }} onClick={() => session.user.role === Roles.SuperAdmin && openModal((props) => SuperAdminInsertMachinesPopup({
                ...props, services, session, defaultValues: {
                  imei: row.imei,
                }, onInsert: ({ id }) => {
                  navigate(`/communal/machine/${id}`)
                }
              }))}>{`IMEI: ${row.imei}`}</p> : null}
              {row.machine_id ? <p style={{ cursor: 'pointer' }}>{row.machine_name}</p> : null}
              <p onClick={go}>{session.translates.for('map.hit_time')}: {formatDate(new Date(row.dt_tracker))}</p>
              {/* <p>{session.translates.for('map.speed')}: {row.speed} km/h</p> */}
              <p onClick={go}>{session.translates.for('map.direction')}: {row.angle}°</p>
              <p>{session.translates.for('map.ucid')}: {row.params.ucid ? row.params.ucid : '-'}</p>
              <p className={hitClassColor(row?.params?.hitX)}>{session.translates.for('map.hit_x')}: {(row.params.hitX).toFixed(2)} FV</p>
              <p className={hitClassColor(row?.params?.hitY)}>{session.translates.for('map.hit_y')}: {(row.params.hitY).toFixed(2)} FV</p>
              {/* <p className={hitClassColor(row?.params?.hitZ)}>{session.translates.for('map.hit_z')}: {(row.params.hitZ).toFixed(2)} FV</p> */}
              {session.user.role === Roles.SuperAdmin && !row.machine_id ? <p className='main-btn' onClick={() => openModal((props) => SuperAdminInsertMachinesPopup({
                ...props, services, session, defaultValues: {
                  imei: row.imei,
                }, onInsert: ({ id }) => {
                  navigate(`/communal/machine/${id}`)
                }
              }))}>{session.translates.for('map.add_btn')}</p> : null}

              {row.machine_id ? <p className='main-btn' onClick={go}>{session.translates.for('map.machine_page')}</p> : null}
            </div>
          </Popup>
        </CircleMarker>
      })}

      {myposition ? <Marker
        key='my-location'
        position={myposition}
        icon={L.divIcon({
          className: 'car-icon',
          html: `<img src="/public/img/gps/i_am_here.png" style="width: 32px; margin: -16px auto auto 0px" />`,
          iconSize: [32, 32], // Adjust size as needed
          iconAnchor: [16, 16], // Anchor point
        })}></Marker> : null}
    </MapContainer>
  )
}

